import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useFilters } from '@elfsight/widget-filters';
import { useSettings } from '@wu/widget-core';
import { useTranslation } from 'react-i18next';
import { MIN_WIDTH_FOR_FILTERS_STYLE } from 'widgets/job-board/src/constants';
import type {
  FilterKey,
  FiltersStyle,
  Job,
  SalaryPeriod
} from '../../../types';
import { useLayout } from '../../layout';
import { useVisibleJobs } from '../../utils';
import type { JobFiltersScheme } from '../types';
import {
  applyFilters,
  getActiveFilters,
  matchesQuery,
  useCurrencyAndPeriodSelection
} from '../utils';
import { useSearch } from './SearchProvider';

type ActiveFilter = {
  id: string;
  label: string;
  field: FilterKey;
};

type JobFilterContext = {
  activeFilters: ActiveFilter[];
  filteredJobs: Job[];
  processedJobs: Job[];
  hasActiveFiltersOrSearch: boolean;
  currentFiltersStyle: FiltersStyle;
  selectedCurrency: string;
  selectedPeriod: SalaryPeriod;
  setSelectedCurrency: (currency: string) => void;
  setSelectedPeriod: (period: SalaryPeriod) => void;
};

const JobFilterContext = createContext<JobFilterContext | null>(null);

export function JobFilterProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const { filtersStyle, filters: filtersSettings } = useSettings();
  const { widgetContainerWidth } = useLayout();
  const visibleJobs = useVisibleJobs();

  const [currentFiltersStyle, setCurrentFiltersStyle] = useState(filtersStyle);
  const [hasActiveFiltersOrSearch, setHasActiveFiltersOrSearch] =
    useState(false);

  const { appliedFilters, filters, resetFilters } =
    useFilters<JobFiltersScheme>();
  const { searchValue } = useSearch();

  const {
    selectedCurrency,
    selectedPeriod,
    setSelectedCurrency,
    setSelectedPeriod
  } = useCurrencyAndPeriodSelection();

  const activeFilters = useMemo(
    () => getActiveFilters(filtersSettings, visibleJobs, t),
    [filtersSettings, visibleJobs, t]
  );

  useEffect(resetFilters, [activeFilters, resetFilters]);

  useEffect(() => {
    const hasAppliedFilters = Object.values(filters).some(
      (arr) => Array.isArray(arr) && arr.length
    );

    const isActive = hasAppliedFilters || searchValue.trim().length > 0;
    setHasActiveFiltersOrSearch(isActive);
  }, [filters, searchValue]);

  const { filteredJobs, processedJobs } = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    const getFilteredJobs = (filterSet: JobFiltersScheme) =>
      visibleJobs.filter(
        (job) => applyFilters(job, filterSet) && matchesQuery(job, query)
      );

    return {
      filteredJobs: getFilteredJobs(appliedFilters),
      processedJobs: getFilteredJobs(filters)
    };
  }, [visibleJobs, appliedFilters, filters, searchValue, applyFilters]);

  useEffect(() => {
    if (widgetContainerWidth < MIN_WIDTH_FOR_FILTERS_STYLE) {
      setCurrentFiltersStyle('popup');
      return;
    }

    if (!activeFilters.length) {
      setCurrentFiltersStyle('inline');
      return;
    }

    setCurrentFiltersStyle(filtersStyle);
  }, [widgetContainerWidth, filtersStyle, activeFilters.length]);

  const value = useMemo(() => {
    return {
      activeFilters,
      filteredJobs,
      processedJobs,
      hasActiveFiltersOrSearch,
      currentFiltersStyle,
      selectedCurrency,
      selectedPeriod,
      setSelectedCurrency,
      setSelectedPeriod
    };
  }, [
    activeFilters,
    filteredJobs,
    processedJobs,
    hasActiveFiltersOrSearch,
    currentFiltersStyle,
    selectedCurrency,
    selectedPeriod,
    setSelectedCurrency,
    setSelectedPeriod
  ]);

  return (
    <JobFilterContext.Provider value={value}>
      {children}
    </JobFilterContext.Provider>
  );
}

export function useJobFilters() {
  const context = useContext(JobFilterContext);
  if (!context) {
    throw new Error('useJobFilters must be used within JobFilterProvider');
  }

  return context;
}
